import React from 'react'
import {
  CheckboxProps,
  Box,
  Input,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Badge,
  Flex,
  Stack,
  IconButton
} from '@chakra-ui/core'
import { useField } from 'formik'
import { Text } from '../../../typography'
import { LabelProps } from '../styles'
import {
  DataListOptionEntity,
  Maybe,
  useFindDataListOptionWithCountQuery
} from '../../../generated/graphql'
import { uniq } from 'lodash'

type ConnectedDatalistProps = LabelProps &
  CheckboxProps & {
    label?: string
    name: string
    listId: string
  }

const ConnectedDatalist: React.FC<ConnectedDatalistProps> = ({ label, name, listId, ...props }) => {
  const [field] = useField(name)
  const [searchTerm, setSearchTerm] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)
  // Archived items must not be offered for new submissions. The server
  // defaults to active-only when no status is named, but this app can run
  // against an older backend and serves cached responses offline — filter
  // on both sides so an archived item never becomes selectable.
  const filters = {
    data_list: {
      id: {
        $eq: listId
      },
      status: { $eq: 'active' }
    },
    status: { $eq: 'active' },
    value: {
      $containsi: searchTerm
    }
  }
  const { data, refetch } = useFindDataListOptionWithCountQuery({
    variables: {
      filters,
      pagination: {
        limit: 10,
        page: 1
      }
    }
  })
  const dataListOptions = data?.findDataListOptionWithCount?.data || []
  const dataListOptionsCount = data?.findDataListOptionWithCount?.count || 0
  const fieldValue = field?.value || []

  const [areOptionsOpen, setAreOptionsOpen] = React.useState(false)
  const [selectedOptions, setSelectedOptions] = React.useState<Maybe<DataListOptionEntity>[]>([])

  React.useEffect(() => {
    if (searchTerm) {
      ;(async () =>
        await refetch({
          filters,
          pagination: {
            limit: 100,
            page: 1
          }
        }))()
    }
    // eslint-disable-next-line
  }, [searchTerm, listId])

  return (
    <Box position="relative" {...props}>
      {label && (
        <Text fontSize="sm" mb={2}>
          {label}
        </Text>
      )}
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Icon name="search" color="gray.300" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          variant="filled"
          placeholder="Search options..."
          onFocus={() => {
            if (inputRef.current) {
              inputRef.current.select()
            }
            setAreOptionsOpen(true)
          }}
          onChange={(e) => {
            const target = e.target as HTMLInputElement
            if (target.value === '') {
              setAreOptionsOpen(false)
            } else {
              setAreOptionsOpen(true)
            }
            setSearchTerm(target.value)
          }}
        />

        <InputRightElement
          children={
            <Icon name={areOptionsOpen ? 'chevron-up' : 'chevron-down'} color="green.500" />
          }
          onClick={() => setAreOptionsOpen(!areOptionsOpen)}
        />
      </InputGroup>

      {areOptionsOpen && (
        <Box position="absolute" zIndex={988} bg="white" w="100%" mt={2} py={2} shadow="md">
          <Flex justifyContent="space-between" px={2} alignItems="center">
            <Text fontSize="xs" color="gray.400">
              {dataListOptionsCount} options found, showing {dataListOptions.length} results
            </Text>
            <IconButton
              icon="close"
              size="sm"
              fontSize="xs"
              onClick={() => {
                setAreOptionsOpen(false)
                setSearchTerm('')
                inputRef.current?.blur()
              }}
              aria-label="Close options"
            />
          </Flex>
          <Box maxH={200} overflowY="auto">
            {dataListOptions?.map((option) => {
              const isSelected = selectedOptions.some(
                (selectedOption) => selectedOption?.id === option?.id
              )
              return (
                <Box
                  key={option?.id}
                  onClick={() => {
                    setSelectedOptions((prev) => [...prev, option])
                    const currentValues = field.value?.values || []
                    const newValues = uniq([...currentValues, option?.attributes?.value])
                    field.onChange({
                      target: { name: field.name, value: { listId, values: newValues } }
                    })
                  }}
                  cursor="pointer"
                  bg={isSelected ? 'green.50' : 'transparent'}
                  border="1px solid"
                  borderColor={isSelected ? 'green.100' : 'transparent'}
                  p={2}
                  rounded="md"
                  color="gray.700"
                  fontSize="sm"
                >
                  {option?.attributes?.value}
                </Box>
              )
            })}
          </Box>
        </Box>
      )}
      {fieldValue?.values?.length > 0 && (
        <Stack flexWrap="wrap" alignItems="flex-start" mt={2} direction="row">
          {fieldValue?.values?.map((value: string, index: number) => {
            return (
              <Badge p={2} rounded="md" variantColor="green" m={1} fontSize="x-small" key={index}>
                {value}
                <Icon
                  name="close"
                  ml={2}
                  cursor="pointer"
                  onClick={() => {
                    setSelectedOptions((prev) =>
                      prev.filter((option) => option?.attributes?.value !== value)
                    )
                    const currentValues = field.value?.values || []
                    const newValues = currentValues.filter((v: any) => v !== value)
                    field.onChange({
                      target: { name: field.name, value: { listId, values: newValues } }
                    })
                  }}
                />
              </Badge>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

export default ConnectedDatalist
